import { EiCategory, TaxStrategy, TreasuryProfile } from '@shared/types';

/**
 * LOT 7E — niveau de confiance d'une estimation financière produit.
 *
 * - indicative   : approximation rapide (NET_RATES, ou fiscality.ts avec des
 *                  paramètres essentiels non renseignés et donc devinés par
 *                  défaut — voir resolveStrategy()/eiCategory dans fiscality.ts).
 * - consolidated : fiscality.ts avec les paramètres essentiels du statut
 *                  explicitement renseignés. Reste une estimation produit,
 *                  jamais un calcul réglementaire certifié (le D4 SSI reste
 *                  ouvert, cf. docs/HUB-LOT7D-REGULATORY-CLOSURE.md).
 * - unavailable  : données insuffisantes pour calculer quoi que ce soit
 *                  (statut ou revenu manquant/nul).
 *
 * Volontairement minimal : ceci n'est PAS un moteur de règles ni un
 * framework de confiance généralisé, juste une fonction pure de sélection.
 */
export type FinancialConfidenceLevel = 'indicative' | 'consolidated' | 'unavailable';

export const FINANCIAL_CONFIDENCE_LABELS: Record<FinancialConfidenceLevel, string> = {
  indicative: 'Estimation indicative',
  consolidated: 'Estimation consolidée',
  unavailable: 'Estimation indisponible',
};

/**
 * Champs essentiels manquant desquels fiscality.ts devine une valeur par
 * défaut (resolveStrategy() pour sasu/eurl, `input.eiCategory || 'bnc-liberal'`
 * pour ei) plutôt que d'utiliser une donnée réellement renseignée par
 * l'utilisateur. Portage n'a pas de stratégie ambiguë (toujours 'remuneration').
 */
export const CONSOLIDATED_REQUIRED_FIELDS: Record<TreasuryProfile['status'], Array<'eiCategory' | 'taxStrategy'>> = {
  ei: ['eiCategory'],
  sasu: ['taxStrategy'],
  eurl: ['taxStrategy'],
  portage: [],
};

export interface FinancialConfidenceInput {
  status?: TreasuryProfile['status'] | null;
  revenue?: number | null;
  eiCategory?: EiCategory | null;
  taxStrategy?: TaxStrategy | null;
}

export function selectFinancialConfidence(input: FinancialConfidenceInput): FinancialConfidenceLevel {
  if (!input.status || !Number.isFinite(input.revenue) || (input.revenue ?? 0) <= 0) return 'unavailable';

  const required = CONSOLIDATED_REQUIRED_FIELDS[input.status] || [];
  const missing = required.some((field) => !input[field]);
  return missing ? 'indicative' : 'consolidated';
}
